/* eslint-disable react-refresh/only-export-components -- exported as InfoDrawerKpis.Default namespace to match call sites */
import { ReactNode } from 'react';
import { Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { InfoContentItem } from './InfoContentItem';
import { MeasureInfoContentItem } from './MeasureInfoContentItem';
import { OrderBasedKpiNote } from './InfoDrawerKpiNote';
import { InfoDrawerKpiEntry, splitInfoDrawerKpiEntries } from './InfoDrawerKpiEntry';

function KpiEntryItem({
  entry,
  isOrderDate,
  highlighted = false,
}: {
  entry: InfoDrawerKpiEntry;
  isOrderDate: boolean;
  highlighted?: boolean;
}) {
  return entry.kind === 'measure' ? (
    <MeasureInfoContentItem
      measure={entry.measure}
      isOrderDate={entry.isOrderDate ?? isOrderDate}
      highlighted={highlighted}
    />
  ) : (
    <InfoContentItem
      titleId={entry.titleId}
      title={entry.title}
      descriptionId={entry.descriptionId}
      description={entry.description}
      highlighted={highlighted}
      measure={entry.measure}
    />
  );
}

function RecentChanges({ entries, isOrderDate }: { entries: InfoDrawerKpiEntry[]; isOrderDate: boolean }) {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <Typography variant="body1" sx={{ mb: 3, color: '#027cc6', fontWeight: 400 }}>
        <FormattedMessage id="info.content.recent_changes" />
      </Typography>
      {entries.map(entry => (
        <KpiEntryItem key={entry.key} entry={entry} isOrderDate={isOrderDate} highlighted />
      ))}
    </div>
  );
}

function KpiList({ entries, isOrderDate }: { entries: InfoDrawerKpiEntry[]; isOrderDate: boolean }) {
  return (
    <>
      {entries.map(entry => (
        <KpiEntryItem key={entry.key} entry={entry} isOrderDate={isOrderDate} />
      ))}
    </>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
      {children}
    </Typography>
  );
}

function Default({
  kpis,
  whyUsefulTitleId = 'info.content.why_is_this_view_useful',
  whyUsefulDescriptionId,
  isOrderDate = true,
  showOrderBasedNote = false,
  children = undefined,
}: {
  kpis: InfoDrawerKpiEntry[];
  whyUsefulTitleId?: string;
  whyUsefulDescriptionId: string;
  isOrderDate?: boolean;
  showOrderBasedNote?: boolean;
  children?: ReactNode;
}) {
  const { recentlyChangedEntries, kpiEntries } = splitInfoDrawerKpiEntries(kpis);

  return (
    <div>
      <RecentChanges entries={recentlyChangedEntries} isOrderDate={isOrderDate} />
      {recentlyChangedEntries.length > 0 && <hr className="my-6 border-snow-200" />}

      <SectionHeading>
        <FormattedMessage id="info.content.helpful_information" defaultMessage="Helpful information" />
      </SectionHeading>
      <InfoContentItem titleId={whyUsefulTitleId} descriptionId={whyUsefulDescriptionId} />

      <hr className="my-6 border-snow-200" />

      <SectionHeading>
        <FormattedMessage id="info.content.kpis" defaultMessage="Key Performance Indicators (KPIs)" />
      </SectionHeading>
      {showOrderBasedNote && <OrderBasedKpiNote />}

      <KpiList entries={kpiEntries} isOrderDate={isOrderDate} />

      {children}
    </div>
  );
}

export const InfoDrawerKpis = {
  Default,
};
